import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, setDoc, getDoc, updateDoc, runTransaction, increment, addDoc, arrayUnion, query, where, getDocs, serverTimestamp, Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { FaUserPlus } from 'react-icons/fa';

const AddDisciple = () => {
  const { currentUser } = useAuth();
  const [discipleData, setDiscipleData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: ''
  });
  const [isChild, setIsChild] = useState(false);
  const [hasDevice, setHasDevice] = useState(false);
  const [userName, setUserName] = useState('');
  const [userAppID, setUserAppID] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [existingDisciple, setExistingDisciple] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showFaithJourneyModal, setShowFaithJourneyModal] = useState(false);
  const [newDiscipleId, setNewDiscipleId] = useState(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserName(userData.name);
            setUserAppID(userData.appID); // Fetch AppID for tracking
          } else {
            console.error('User data not found');
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDiscipleData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleVerifyPhone = async (phoneNumber) => {
    try {
      setVerificationError(null);
      
      // Format phone number to E.164 format
      const formattedPhone = phoneNumber.startsWith('+') 
        ? phoneNumber 
        : `+1${phoneNumber.replace(/\D/g, '')}`;
      
      console.log('Attempting to verify phone:', formattedPhone);
      console.log('API URL:', 'http://localhost:5001/api/send-verification');
      
      const response = await fetch('http://localhost:5001/api/send-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: formattedPhone }),
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (data.success) {
        setShowVerificationModal(true);
      } else {
        setVerificationError(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending verification:', error);
      setVerificationError('Failed to send verification code. Please try again.');
    }
  };

  const handleVerifyCode = async () => {
    try {
      setVerificationError(null);
      
      if (!verificationCode) {
        setVerificationError('Please enter the verification code');
        return;
      }
      
      const formattedPhone = discipleData.phone.startsWith('+')
        ? discipleData.phone
        : `+1${discipleData.phone.replace(/\D/g, '')}`;

      const response = await fetch('http://localhost:5001/api/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: formattedPhone,
          code: verificationCode,
        }),
      });

      const data = await response.json();
      
      if (data.valid) {
        setPhoneVerified(true);
        setShowVerificationModal(false);
        setVerificationCode('');
      } else {
        setVerificationError('Invalid verification code. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setVerificationError('Failed to verify code. Please try again.');
    }
  };

  const createInitialReminder = async (discipleData, userId) => {
    const reminderQueueRef = collection(db, 'users', userId, 'reminderQueue');
    
    // Get the mentorDetails to get the actual frequency and preferred time
    const mentorDetailsRef = doc(db, 'disciples', discipleData.id, 'mentorDetails', userId);
    const mentorDetailsSnap = await getDoc(mentorDetailsRef);
    const mentorSettings = mentorDetailsSnap.data()?.reminderSettings || {};
    
    const frequency = mentorSettings.frequency || 7; // days
    const preferredTime = mentorSettings.preferredTime || "09:00";
    
    // Calculate initial scheduled time using preferred time
    const scheduledFor = new Date();
    scheduledFor.setDate(scheduledFor.getDate() + 1); // Start tomorrow
    
    // Apply the preferred time
    if (preferredTime) {
      const [hour, minute] = preferredTime.split(':').map(Number);
      scheduledFor.setHours(hour, minute, 0, 0);
    } else {
      scheduledFor.setHours(9, 0, 0, 0); // Fallback to 9am
    }

    const reminderData = {
      discipleId: discipleData.id,
      discipleName: discipleData.name,
      mentorId: userId,
      message: `Time to connect with ${discipleData.name}`,
      verseReference: null,
      frequency_at_creation: frequency,
      interval: frequency,
      preferredTime: preferredTime,
      scheduledFor: Timestamp.fromDate(scheduledFor),
      createdAt: Timestamp.now(),
      queueStatus: 'queued',
      reminderStatus: 'scheduled',
      archived: false,
      completedAt: null,
      snoozedAt: null,
      snoozedUntil: null,
      cancelledAt: null,
      resentAt: null,
      archivedAt: null,
      completionReason: null,
      notificationsSent: 0,
      messageSent: false,
      messageSentAt: null,
      messageType: null,
      messageDetails: null,
      error: null,
      nextReminderCreated: null,
      attempts: 0,
    };

    try {
      await addDoc(reminderQueueRef, reminderData);
    } catch (error) {
      console.error('Error creating initial reminder:', error);
      throw error;
    }
  };

  const handleAddDisciple = async (e) => {
    e.preventDefault();
    
    // Validation: Age confirmation required for non-children
    if (!isChild && !ageConfirmed) {
      setMessage('Please confirm that the disciple is 18 years of age or older.');
      return;
    }

    // Validation: Phone is required only if not a child, or if child has device
    const phoneRequired = !isChild || (isChild && hasDevice);
    if (phoneRequired && !discipleData.phone) {
      setMessage('Phone number is required');
      return;
    }
    
    // Validation: Email is required for non-children
    if (!isChild && !discipleData.email) {
      setMessage('Email is required');
      return;
    }
    
    setLoading(true);
    setMessage('');

    try {
      // Check if a disciple with the same email already exists
      const disciplesQuery = query(collection(db, 'disciples'), where('email', '==', discipleData.email));
      const querySnapshot = await getDocs(disciplesQuery);

      if (!querySnapshot.empty) {
        const existingDiscipleDoc = querySnapshot.docs[0];
        const existingDiscipleData = existingDiscipleDoc.data();
        setExistingDisciple({ id: existingDiscipleDoc.id, ...existingDiscipleData });
        setShowModal(true);
      } else {
        // Increment disciple counter and set new disciple document ID
        const counterRef = doc(db, 'counters', 'discipleCounter');
        const newDiscipleId = await runTransaction(db, async (transaction) => {
          const counterSnap = await transaction.get(counterRef);
          let newCount = 1;
          if (counterSnap.exists()) {
            newCount = counterSnap.data().count + 1;
          } else {
            await transaction.set(counterRef, { count: newCount });
          }
          transaction.update(counterRef, { count: increment(1) });
          return `discID_${String(newCount).padStart(3, '0')}`;
        });

        // Add disciple to the 'disciples' collection with ONLY the basic fields
        const discipleRef = doc(db, 'disciples', newDiscipleId);
        const discipleDocData = {
          name: discipleData.name,
          email: discipleData.email,
          phone: discipleData.phone,
          phoneVerified: phoneVerified || false,
          gender: discipleData.gender,
          createdBy: userAppID,
          createdAt: Timestamp.now(),
          id: newDiscipleId
        };
        
        // Add isChild and hasDevice
        discipleDocData.isChild = isChild;
        if (isChild) {
          discipleDocData.hasDevice = hasDevice;
        }
        
        // Phone is required only if not a child, or if child has device
        const phoneRequired = !isChild || (isChild && hasDevice);
        if (phoneRequired && discipleData.phone) {
          discipleDocData.phone = discipleData.phone;
        } else if (!isChild) {
          // For non-children, phone should always be included (validation ensures it's filled)
          discipleDocData.phone = discipleData.phone;
        }
        // If child without device, phone is optional - don't include if empty
        
        // Email is optional for children, required for non-children
        if (discipleData.email) {
          discipleDocData.email = discipleData.email;
        }
        
        await setDoc(discipleRef, discipleDocData);

        // Fetch user's global default preferred time
        const userRefForDefault = doc(db, 'users', currentUser.uid);
        const userDocForDefault = await getDoc(userRefForDefault);
        const globalDefaultTime = userDocForDefault.data()?.notificationPreferences?.preferredReminderTime || "09:00";

        // Create the mentorDetails subcollection with mentor-specific fields
        const mentorDetailsRef = doc(discipleRef, 'mentorDetails', userAppID);
        await setDoc(mentorDetailsRef, {
          appID: userAppID,
          name: userName,
          contactCardSent: false,
          lastInteractionAt: null,
          photoURL: '',
          archived: false,
          reminderSettings: {
            enabled: true,
            frequency: 7,
            preferredTime: globalDefaultTime,
            lastMessageDate: null,
            nextReminderDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            quietHours: {
              start: "22:00",
              end: "08:00"
            }
          },
          // Add Faith Journey Defaults for NEW disciple
          faithJourneyStage: null,
          faithJourneyAgeRange: null,
          faithJourneyExperience: null,
          faithJourneyRelationship: null,
          faithJourneyContext: '',
          faithJourneyStageComment: '',
          faithJourneyAgeRangeComment: '',
          faithJourneyExperienceComment: '',
          faithJourneyRelationshipComment: '',
          // Missional scale will default in component if not set
          addedToMentorOn: serverTimestamp(),
          createdAt: serverTimestamp(), // Use server timestamp for creation
        });

        // Note: Removed redundant userDiscipleSettings creation - all settings now in mentorDetails

        // Initialize collections under mentorDetails
        // 1. Email Messages Collection
        const messagesRef = collection(mentorDetailsRef, 'messages');
        await addDoc(messagesRef, {
          placeholder: true,
          type: 'email',
          createdAt: new Date()
        });

        // 2. SMS Messages Collection
        const messagesSMSRef = collection(mentorDetailsRef, 'messagesSMS');
        await addDoc(messagesSMSRef, {
          placeholder: true,
          type: 'sms',
          createdAt: new Date()
        });

        // 3. Notes Collection
        const notesRef = collection(mentorDetailsRef, 'notes');
        await addDoc(notesRef, {
          note: 'Initial note for disciple',
          createdAt: new Date(),
          createdBy: userAppID,
          archived: false, // Field to track if note is archived
        });

        // 2. Create initial reminder
        await createInitialReminder({
          id: newDiscipleId,
          name: discipleData.name
        }, currentUser.uid);

        // Update the user's document to add the disciple ID
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          discipleIds: arrayUnion(newDiscipleId),
        });

        setNewDiscipleId(newDiscipleId);
        setShowFaithJourneyModal(true);
      }
    } catch (error) {
      console.error('Error adding disciple:', error);
      setMessage('Failed to add disciple. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExistingDisciple = async () => {
    setLoading(true);
    try {
      // Add the existing disciple to the current user's disciple list
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        discipleIds: arrayUnion(existingDisciple.id),
      });
  
      // Create mentorDetails document with mentor-specific fields
      const mentorDetailsRef = doc(db, 'disciples', existingDisciple.id, 'mentorDetails', userAppID);
      await setDoc(mentorDetailsRef, {
        appID: userAppID,
        name: userName,
        contactCardSent: false,
        lastInteractionAt: null,
        photoURL: '',
        archived: false,
        reminderSettings: {
          enabled: true,
          frequency: 7,
          preferredTime: "09:00",
          lastMessageDate: null,
          nextReminderDate: null,
          notificationMethod: 'fcm',
          fcmToken: null,
          phoneNumber: existingDisciple.phone || null,
          quietHours: {
            start: "22:00",
            end: "08:00"
          }
        },
        // Add Faith Journey Defaults when adding EXISTING disciple to NEW mentor
        faithJourneyStage: null,
        faithJourneyAgeRange: null,
        faithJourneyExperience: null,
        faithJourneyRelationship: null,
        faithJourneyContext: '',
        faithJourneyStageComment: '',
        faithJourneyAgeRangeComment: '',
        faithJourneyExperienceComment: '',
        faithJourneyRelationshipComment: '',
        // Missional scale will default in component if not set
        addedToMentorOn: serverTimestamp(),
        createdAt: serverTimestamp(), // Use server timestamp for creation
      });

      // Initialize collections for the new mentor
      const messagesRef = collection(mentorDetailsRef, 'messages');
      const messagesSMSRef = collection(mentorDetailsRef, 'messagesSMS');
      const notesRef = collection(mentorDetailsRef, 'notes');

      // Check and initialize collections if empty
      const [messagesSnapshot, messagesSMSSnapshot, notesSnapshot] = await Promise.all([
        getDocs(messagesRef),
        getDocs(messagesSMSRef),
        getDocs(notesRef)
      ]);

      if (messagesSnapshot.empty) {
        await addDoc(messagesRef, {
          placeholder: true,
          type: 'email',
          createdAt: new Date()
        });
      }

      if (messagesSMSSnapshot.empty) {
        await addDoc(messagesSMSRef, {
          placeholder: true,
          type: 'sms',
          createdAt: new Date()
        });
      }

      if (notesSnapshot.empty) {
        await addDoc(notesRef, {
          note: 'Initial note for disciple',
          createdAt: new Date(),
          createdBy: userAppID,
          archived: false,
        });
      }

      setMessage('Disciple added to your list successfully!');
      setShowModal(false);
      navigate(`/disciple-menu/${existingDisciple.id}`);
    } catch (error) {
      console.error('Error adding existing disciple:', error);
      setMessage('Failed to add existing disciple. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto mt-6 p-8 bg-white shadow-lg rounded-lg max-w-screen-md">
        <div className="flex items-center mb-8 border-b pb-4">
          <div className="p-3 bg-blue-100 rounded-full mr-4">
            <FaUserPlus className="text-blue-600 text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-primary">Add a New Disciple</h1>
            <p className="text-gray-600 text-sm mt-1">
              Track and communicate with your disciples through our platform
            </p>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg mb-8">
          <p className="text-gray-700 text-sm leading-relaxed">
            When you add a disciple, they'll appear on your dashboard for tracking and communication. 
            If this person is new to the system, they'll be added to our database. Multiple mentors 
            can disciple the same person, so if they're already in the system, you can add them to 
            your list of disciples while maintaining their existing mentoring relationships.
          </p>
        </div>

        <form onSubmit={handleAddDisciple} className="space-y-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <input
              type="text"
              name="name"
              value={discipleData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm transition duration-150"
              placeholder="Enter disciple's name"
              required
            />
          </div>

          {/* Child disciple feature disabled for now - reading to kids on mobile is turned off
          <div className="mb-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isChild}
                onChange={(e) => {
                  const newIsChild = e.target.checked;
                  setIsChild(newIsChild);
                  if (!newIsChild) {
                    setHasDevice(false);
                  } else {
                    setAgeConfirmed(false);
                  }
                }}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Is this a child?
              </span>
            </label>
          </div>

          {isChild && (
            <div className="mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasDevice}
                  onChange={(e) => setHasDevice(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700 block">
                    Do they have a wi-fi device?
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    If yes, they can receive content via SMS/email. If no, you can read content together in the app.
                  </p>
                </div>
              </label>
            </div>
          )}
          */}

          {/* Email - Show if not a child, or if child has device */}
          {(!isChild || (isChild && hasDevice)) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email {!isChild && <span className="text-red-500">*</span>}
              </label>
              <input
                type="email"
                name="email"
                value={discipleData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm transition duration-150"
                placeholder="Enter disciple's email"
                required={!isChild}
              />
            </div>
          )}

          {/* Phone - Show if not a child, or if child has device */}
          {(!isChild || (isChild && hasDevice)) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number {!isChild && <span className="text-red-500">*</span>}
              </label>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <input
                  type="tel"
                  name="phone"
                  value={discipleData.phone}
                  onChange={handleChange}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm transition duration-150"
                  placeholder="Enter disciple's phone number"
                  required={!isChild}
                />
                {discipleData.phone && !phoneVerified && (
                  <button
                    type="button"
                    onClick={() => handleVerifyPhone(discipleData.phone)}
                    className="bg-green-600 text-white px-4 sm:px-6 py-2 rounded-md hover:bg-green-700 text-sm font-medium transition duration-150 shadow-sm whitespace-nowrap"
                  >
                    Verify
                  </button>
                )}
                {phoneVerified && (
                  <span className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-md whitespace-nowrap">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    Verified
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
            <select
              name="gender"
              value={discipleData.gender}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm transition duration-150"
              required
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          
          {/* Age Confirmation - only shown for non-children */}
          {!isChild && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  I confirm that this disciple is 18 years of age or older.
                </span>
              </label>
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || (!isChild && !ageConfirmed)}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding...
                </span>
              ) : (
                <span className="flex items-center">
                  <FaUserPlus className="mr-2" />
                  Add Disciple
                </span>
              )}
            </button>
          </div>
        </form>

        {message && (
          <div className="mt-4 p-4 rounded-md bg-blue-50 text-blue-700 text-sm text-center">
            {message}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h2 className="text-lg font-bold mb-4">Disciple Already Exists</h2>
            <p className="text-sm text-gray-600 mb-6">
              We already have this disciple email on file. Do you want to add "{existingDisciple.name}" to your list of disciples?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowModal(false)}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddExistingDisciple}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                disabled={loading}
              >
                {loading ? 'Adding...' : 'Add to My Disciples'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVerificationModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full m-4">
            <h2 className="text-lg font-semibold mb-4">Verify Phone Number</h2>
            <p className="mb-6 text-gray-700">
              We have sent a verification code to your phone number. Please enter the code below.
            </p>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="w-full px-3 py-2 border rounded mb-4"
              placeholder="Enter verification code"
            />
            {verificationError && (
              <p className="text-red-500 text-sm mb-4">{verificationError}</p>
            )}
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowVerificationModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyCode}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Verify
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Faith Journey Modal */}
      {showFaithJourneyModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-bold mb-4">Tell us more about your disciple?</h2>
            <p className="mb-6 text-gray-700">Would you like to fill out your disciple's Faith Journey now? This helps us personalize resources and support for your mentoring relationship. You can always do this later from the disciple menu.</p>
            <div className="flex justify-center gap-4 mt-6">
              <button
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-base font-medium"
                onClick={() => navigate(`/faith-journey/${newDiscipleId}`)}
              >
                Yes, let's do it!
              </button>
              <button
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-base font-medium"
                onClick={() => navigate(`/disciple-menu/${newDiscipleId}`)}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDisciple;
